import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TestTube2, Play, AlertCircle, CheckCircle2, Clock, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import FormTester from '@/components/debug/FormTester';

export default function AdminFormTesting() {
  const { isAdmin, isLoading: authLoading } = useAdminAuth();

  if (authLoading) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="flex items-center gap-3">
          <Clock className="h-5 w-5 animate-spin" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">You need admin privileges to access form testing.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <TestTube2 className="h-6 w-6" />
          <h1 className="text-3xl font-bold">Form Testing</h1>
        </div>
        <p className="text-muted-foreground">
          Test all form submissions with dummy data and verify email delivery.
        </p>
      </div>

      {/* Quick Links */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="h-5 w-5" />
            Quick Links
          </CardTitle>
          <CardDescription>
            Access related admin pages to view submitted test data
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/forms/contact">Contact Forms</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/forms/early-adopters">Early Adopters</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/forms/professionals">Professionals</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/forms/caregivers">Caregivers</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/forms/sponsors">Sponsors</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/forms/stories">Stories</Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Test Instructions */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Play className="h-5 w-5" />
            Testing Instructions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-3">
            <Badge variant="outline" className="mt-1">1</Badge>
            <div>
              <p className="font-medium">Run Tests</p>
              <p className="text-sm text-muted-foreground">
                Click "Test All Forms" to submit dummy data to all forms
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Badge variant="outline" className="mt-1">2</Badge>
            <div>
              <p className="font-medium">Check Email Delivery</p>
              <p className="text-sm text-muted-foreground">
                Monitor the test email addresses for confirmation emails
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Badge variant="outline" className="mt-1">3</Badge>
            <div>
              <p className="font-medium">Verify Data</p>
              <p className="text-sm text-muted-foreground">
                Use the quick links above to check that test data appears in admin forms
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Tester Component */}
      <FormTester />
    </div>
  );
}